package com.dotdrives.web.pages.settings

import com.dotdrives.controller.SettingsController
import com.dotdrives.controller.SettingsTab
import com.dotdrives.controller.SettingsGroup
import com.dotdrives.controller.SettingsUiState
import com.dotdrives.design.DotTone
import com.dotdrives.model.OrgMember
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotButton
import react.FC
import react.Key
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.select
import react.dom.html.ReactHTML.option
import react.dom.html.ReactHTML.label
import react.dom.aria.AriaPressed
import web.dom.ElementId
import com.dotdrives.design.DotButtonVariant
import react.dom.html.ReactHTML.div
import react.useEffect
import web.cssom.ClassName
import web.html.ButtonType
import web.html.button as buttonType

external interface SettingsPageProps : Props {
    var workflowController: com.dotdrives.controller.WorkflowController
    var workflowState: com.dotdrives.controller.WorkflowState
    var controller: SettingsController
    var state: SettingsUiState
    var members: List<OrgMember>
    var currentUserRole: String
    /** Actual membership role; billing recovery remains available while operations are paused. */
    var billingRole: String
    var userId: String
    var orgId: String
    var canManageUsers: Boolean
}

private data class SettingsTabEntry(val tab: SettingsTab, val label: String)

private val TAB_ENTRIES = listOf(
    SettingsTabEntry(SettingsTab.UsersRoles, "Members & invitations"),
    SettingsTabEntry(SettingsTab.Process, "Process"),
    SettingsTabEntry(SettingsTab.GoalsBudgets, "Goals"),
    SettingsTabEntry(SettingsTab.Tags, "Tags"),
    SettingsTabEntry(SettingsTab.Integrations, "Integrations"),
    SettingsTabEntry(SettingsTab.Billing, "Billing"),
)

private val GROUPS = listOf(
    SettingsGroup.Team to listOf(SettingsTab.UsersRoles),
    SettingsGroup.Fundraising to listOf(SettingsTab.Process, SettingsTab.GoalsBudgets, SettingsTab.Tags),
    SettingsGroup.Account to listOf(SettingsTab.Integrations, SettingsTab.Billing),
)

val SettingsPage = FC<SettingsPageProps> { props ->
    val state = props.state

    useEffect(props.controller) {
        props.controller.load()
        props.workflowController.loadJobs()
    }

    SettingsStyles()
    SettingsNavigationStyles()
    fun navigateSection(tab: SettingsTab) {
        props.controller.selectTab(tab)
        kotlinx.browser.window.location.hash = com.dotdrives.web.workspaceHash(settingsSectionHash(tab))
    }
    useEffect(props.controller) {
        val listener: () -> Unit = {
            props.controller.selectTab(settingsSectionFromHash(kotlinx.browser.window.location.hash))
        }
        listener()
        kotlinx.browser.window.asDynamic().addEventListener("hashchange", listener)
        try { kotlinx.coroutines.awaitCancellation() }
        finally { kotlinx.browser.window.asDynamic().removeEventListener("hashchange", listener) }
    }
    useEffect(props.workflowState.result) {
        if (props.workflowState.result != null) props.controller.load()
    }

    div {
        className = ClassName("settings-page")

        div {
            className = ClassName("settings-section-picker")
            label { htmlFor = ElementId("settings-section"); +"Settings section" }
            select {
                id = ElementId("settings-section")
                value = settingsSectionHash(state.tab)
                onChange = { navigateSection(settingsSectionFromHash(it.currentTarget.value)) }
                TAB_ENTRIES.forEach { entry -> option { value = settingsSectionHash(entry.tab); +entry.label } }
            }
        }
        div {
            className = ClassName("settings-tabs")
            role = "group".unsafeCast<react.dom.aria.AriaRole>()
            ariaLabel = "Settings sections"
            GROUPS.forEach { (group, tabs) ->
                div {
                    className = ClassName("settings-nav-group")
                    div { className = ClassName("settings-nav-group-title"); +group.name }
                    tabs.forEach { tab ->
                        val entry = TAB_ENTRIES.first { it.tab == tab }
                        button {
                            key = entry.label.unsafeCast<Key>()
                            type = ButtonType.buttonType
                            className = ClassName("settings-tab" + if (entry.tab == state.tab) " settings-tab-active" else "")
                            ariaPressed = (entry.tab == state.tab).unsafeCast<AriaPressed>()
                            onClick = { navigateSection(entry.tab) }
                            +entry.label
                        }
                    }
                }
            }
        }

        billingBanner(state.billingStatus)?.let { banner ->
            DotBanner {
                message = banner.message
                tone = if (banner.tone == "danger") DotTone.Danger else DotTone.Warning
                className = "settings-billing-banner"
            }
        }

        when (state.tab) {
            SettingsTab.Process -> ConfigurationPanel {
                orgId = props.orgId; role = props.currentUserRole; process = true
            }
            SettingsTab.Targets -> Unit
            SettingsTab.UsersRoles -> {
                if (props.canManageUsers) {
                    div {
                        className = ClassName("settings-actions-row")
                        DotButton {
                            text = "Refresh team"
                            variant = DotButtonVariant.Secondary
                            onClick = { props.controller.load(); props.workflowController.loadJobs() }
                        }
                    }
                }
                UsersRolesPanel {
                    workflow = props.workflowController
                    workflowState = props.workflowState
                    controller = props.controller
                    this.state = state
                    canManageUsers = props.canManageUsers
                    currentUserRole = props.currentUserRole
                }
                TeamWorkflowPanel {
                    workflow = props.workflowController
                    workflowState = props.workflowState
                    this.state = state
                    role = props.currentUserRole
                }

            }
            SettingsTab.GoalsBudgets -> div {
                UnifiedGoalsPanel { orgId=props.orgId; role=props.currentUserRole; controller=props.controller; this.state=state; members=props.members; canWrite=props.canManageUsers }
            }
            SettingsTab.Tags -> TagsPanel {
                controller = props.controller
                this.state = state
                canWrite = props.canManageUsers
            }
            SettingsTab.Integrations -> div {
                FiscalPeriodPanel { controller = props.controller; this.state = state; canWrite = props.canManageUsers }
                listOf("stripe-connect", "qbo-connect").forEach { endpoint ->
                    ProviderWorkflowPanel { key = endpoint.unsafeCast<Key>(); orgId = props.orgId; userId = props.userId; role = props.currentUserRole; provider = endpoint }
                }
            }
            SettingsTab.Billing -> if (com.dotdrives.model.canAdminister(props.billingRole)) ProviderWorkflowPanel {
                key = "billing-portal".unsafeCast<Key>(); orgId = props.orgId; userId = props.userId; role = props.billingRole; provider = "billing-portal"
            } else BillingPanel { controller = props.controller; this.state = state }

        }
    }

    if (state.inviteDialogOpen) {
        InviteUserDialog {
            controller = props.controller
            this.state = state
            onClose = props.controller::closeInviteDialog
        }
    }

}
